import { useEffect, useState, useCallback } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { LogOut, RefreshCw, Trash2, Mail, Phone, Building2, User, Calendar } from "lucide-react";
import { useAuth } from "../auth/AuthContext";

const AdminDashboard = () => {
  const { admin, loading, logout, authFetch } = useAuth();
  const loc = useLocation();
  const [items, setItems] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setFetching(true);
    setErr("");
    try {
      const { data } = await authFetch("get", "/admin/consultations");
      setItems(data);
    } catch (e) {
      setErr("Failed to load consultations");
    } finally {
      setFetching(false);
    }
  }, [authFetch]);

  useEffect(() => { if (admin) load(); }, [admin, load]);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this consultation request?")) return;
    try {
      await authFetch("delete", `/admin/consultations/${id}`);
      setItems((arr) => arr.filter((x) => x.id !== id));
    } catch {
      setErr("Delete failed");
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div className="eyebrow">Loading…</div>
      </div>
    );
  }

  if (!admin) return <Navigate to="/admin/login" state={{ from: loc.pathname }} replace />;

  const total = items.length;
  const thisWeek = items.filter((i) => {
    const d = new Date(i.submitted_at);
    return Date.now() - d.getTime() < 7 * 24 * 3600 * 1000;
  }).length;
  const sectors = new Set(items.map((i) => i.sector).filter(Boolean)).size;

  return (
    <section style={{ padding: "64px 0 120px" }} data-testid="admin-dashboard">
      <div className="container-grav">
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16, marginBottom: 48 }}>
          <div>
            <div className="eyebrow" style={{ marginBottom: 12 }}>Admin · Leads</div>
            <h1 className="display-lg">Consultation requests.</h1>
            <p className="body-md" style={{ marginTop: 12 }}>Signed in as <span style={{ color: "var(--ink)", fontWeight: 600 }}>{admin.email}</span></p>
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <button onClick={load} className="btn btn-ghost" style={{ padding: "12px 20px", fontSize: 14 }} data-testid="admin-refresh">
              <RefreshCw size={16} /> Refresh
            </button>
            <button onClick={logout} className="btn" style={{ padding: "12px 20px", fontSize: 14 }} data-testid="admin-logout">
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)", marginBottom: 64 }}>
          {[
            { k: total, l: "Total leads" },
            { k: thisWeek, l: "New this week" },
            { k: sectors, l: "Unique sectors" },
          ].map((s, i, arr) => (
            <div key={s.l} style={{ padding: "32px 24px", borderRight: i < arr.length - 1 ? "1px solid var(--line)" : "none" }}>
              <div className="font-display" style={{ fontSize: 44, fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1 }}>{s.k}</div>
              <div style={{ marginTop: 10, color: "var(--muted)", fontSize: 13 }}>{s.l}</div>
            </div>
          ))}
        </div>

        {/* Leads */}
        {err && <div style={{ color: "#B91C1C", fontSize: 14, border: "1px solid #FCA5A5", padding: "12px 16px", background: "#FEF2F2", marginBottom: 24 }}>{err}</div>}

        {fetching ? (
          <div className="eyebrow" style={{ padding: "64px 0", textAlign: "center" }}>Loading leads…</div>
        ) : items.length === 0 ? (
          <div className="tile" style={{ padding: "64px 40px", textAlign: "center" }}>
            <div className="eyebrow" style={{ marginBottom: 12 }}>Empty inbox</div>
            <h3 className="display-sm">No consultation requests yet.</h3>
            <p className="body-md" style={{ marginTop: 10 }}>New submissions from <Link to="/contact" className="arrow-link" style={{ display: "inline" }}>/contact</Link> will appear here.</p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }} data-testid="admin-leads-list">
            {items.map((it) => (
              <article key={it.id} className="tile" data-testid={`lead-${it.id}`} style={{ padding: "32px 36px" }}>
                <div className="accent-bar" />
                <div className="lead-grid">
                  <div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
                      {it.sector && <span className="chip active" style={{ padding: "4px 12px", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase" }}>{it.sector}</span>}
                      {it.interest && <span className="chip" style={{ padding: "4px 12px", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", borderColor: "var(--ink)", color: "var(--ink)" }}>{it.interest}</span>}
                    </div>
                    <h3 className="display-sm">
                      {it.full_name} · <span style={{ color: "var(--muted)", fontWeight: 500 }}>{it.organization}</span>
                    </h3>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 24, marginTop: 14, color: "var(--muted)", fontSize: 14 }}>
                      <a href={`mailto:${it.email}`} style={{ color: "var(--green)", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8 }}><Mail size={14} /> {it.email}</a>
                      {it.phone && <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Phone size={14} /> {it.phone}</span>}
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Calendar size={14} /> {new Date(it.submitted_at).toLocaleString()}</span>
                    </div>
                    <p style={{ marginTop: 18, color: "var(--ink-2)", fontSize: 15, lineHeight: 1.65, whiteSpace: "pre-wrap" }}>{it.requirements}</p>
                  </div>
                  <button onClick={() => onDelete(it.id)} aria-label="Delete" title="Delete" data-testid={`delete-${it.id}`}
                    style={{ background: "transparent", border: "1px solid var(--line-2)", width: 40, height: 40, borderRadius: "50%", color: "var(--muted)", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", transition: "all 200ms ease", justifySelf: "start" }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#B91C1C"; e.currentTarget.style.color = "#B91C1C"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--line-2)"; e.currentTarget.style.color = "var(--muted)"; }}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default AdminDashboard;
